import type { TriangleMesh } from '../types';
import { compilePlacement, type Placement, type PlacementTransform } from './placement';

/**
 * STL writers.
 *
 * STL carries no unit information; every slicer interprets the numbers as
 * millimetres, which is exactly the unit the mesh builder works in, so the
 * coordinates are written through unchanged.
 */

/** Applies a compiled placement to a point, writing into `out`. */
function place(
  transform: PlacementTransform,
  x: number,
  y: number,
  z: number,
  out: Float32Array,
): void {
  if (!transform.active) {
    out[0] = x;
    out[1] = y;
    out[2] = z;
    return;
  }
  out[0] = x * transform.cos - y * transform.sin + transform.x;
  out[1] = x * transform.sin + y * transform.cos + transform.y;
  out[2] = z + transform.z;
}

/** Face normal of a triangle, or a zero vector for a degenerate one. */
function faceNormal(
  positions: Float32Array,
  a: number,
  b: number,
  c: number,
  out: Float32Array,
): void {
  const ax = positions[a];
  const ay = positions[a + 1];
  const az = positions[a + 2];

  const ux = positions[b] - ax;
  const uy = positions[b + 1] - ay;
  const uz = positions[b + 2] - az;
  const vx = positions[c] - ax;
  const vy = positions[c + 1] - ay;
  const vz = positions[c + 2] - az;

  const nx = uy * vz - uz * vy;
  const ny = uz * vx - ux * vz;
  const nz = ux * vy - uy * vx;

  const length = Math.hypot(nx, ny, nz);
  if (length < 1e-12) {
    out[0] = 0;
    out[1] = 0;
    out[2] = 0;
    return;
  }

  out[0] = nx / length;
  out[1] = ny / length;
  out[2] = nz / length;
}

export interface StlOptions {
  /** Written into the 80-byte header. Truncated to fit. */
  header?: string;
  /**
   * Per-mesh placement, applied on write. STL has no object or transform
   * concept, so the rotation and translation are baked into the coordinates.
   */
  placements?: readonly Placement[];
}

const BINARY_STL_HEADER_BYTES = 80;
const BINARY_STL_TRIANGLE_BYTES = 50;

export function countTriangles(meshes: readonly TriangleMesh[]): number {
  let total = 0;
  for (const mesh of meshes) total += mesh.indices.length / 3;
  return total;
}

/**
 * Binary STL. Several meshes are concatenated into one triangle soup, which is
 * all the format supports — use 3MF when the parts must stay distinguishable.
 */
export function exportBinaryStl(
  meshes: readonly TriangleMesh[],
  options: StlOptions = {},
): ArrayBuffer {
  const triangleCount = countTriangles(meshes);
  const buffer = new ArrayBuffer(
    BINARY_STL_HEADER_BYTES + 4 + triangleCount * BINARY_STL_TRIANGLE_BYTES,
  );
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  const header = options.header ?? 'Organic3D panel';
  for (let i = 0; i < Math.min(header.length, BINARY_STL_HEADER_BYTES - 1); i += 1) {
    bytes[i] = header.charCodeAt(i) & 0x7f;
  }

  view.setUint32(BINARY_STL_HEADER_BYTES, triangleCount, true);

  const normal = new Float32Array(3);
  const placed = new Float32Array(3);
  let cursor = BINARY_STL_HEADER_BYTES + 4;

  for (let m = 0; m < meshes.length; m += 1) {
    const { positions, indices } = meshes[m];
    const transform = compilePlacement(options.placements?.[m]);

    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i] * 3;
      const b = indices[i + 1] * 3;
      const c = indices[i + 2] * 3;

      // The normal is computed before placement and rotated afterwards, which
      // is valid because the placement is a rotation about z plus a
      // translation: no scaling, so normals transform like directions.
      faceNormal(positions, a, b, c, normal);
      const nx = normal[0] * transform.cos - normal[1] * transform.sin;
      const ny = normal[0] * transform.sin + normal[1] * transform.cos;
      view.setFloat32(cursor, nx, true);
      view.setFloat32(cursor + 4, ny, true);
      view.setFloat32(cursor + 8, normal[2], true);
      cursor += 12;

      for (const base of [a, b, c]) {
        place(transform, positions[base], positions[base + 1], positions[base + 2], placed);
        view.setFloat32(cursor, placed[0], true);
        view.setFloat32(cursor + 4, placed[1], true);
        view.setFloat32(cursor + 8, placed[2], true);
        cursor += 12;
      }

      view.setUint16(cursor, 0, true);
      cursor += 2;
    }
  }

  return buffer;
}

/** ASCII STL. Much larger than binary; useful for diffing and for tests. */
export function exportAsciiStl(meshes: readonly TriangleMesh[], options: StlOptions = {}): string {
  const name = (options.header ?? 'organic3d').replace(/\s+/g, '_');
  const lines: string[] = [`solid ${name}`];
  const normal = new Float32Array(3);
  const placed = new Float32Array(3);

  const format = (value: number): string => (Math.round(value * 1e6) / 1e6).toExponential(6);

  for (let m = 0; m < meshes.length; m += 1) {
    const { positions, indices } = meshes[m];
    const transform = compilePlacement(options.placements?.[m]);

    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i] * 3;
      const b = indices[i + 1] * 3;
      const c = indices[i + 2] * 3;

      faceNormal(positions, a, b, c, normal);
      lines.push(
        `  facet normal ${format(normal[0] * transform.cos - normal[1] * transform.sin)} ${format(
          normal[0] * transform.sin + normal[1] * transform.cos,
        )} ${format(normal[2])}`,
        '    outer loop',
      );
      for (const base of [a, b, c]) {
        place(transform, positions[base], positions[base + 1], positions[base + 2], placed);
        lines.push(`      vertex ${format(placed[0])} ${format(placed[1])} ${format(placed[2])}`);
      }
      lines.push('    endloop', '  endfacet');
    }
  }

  lines.push(`endsolid ${name}`, '');
  return lines.join('\n');
}
